import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCcw, DollarSign, Clock, HelpCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const Refund = () => {
    const { language } = useLanguage();

    const texts = {
        tr: {
            title: 'İade ve İptal Politikası',
            lastUpdated: 'Son Güncelleme: 25 Mart 2026',
            sections: [
                {
                    title: '1. Dijital Hizmetler',
                    content: 'Cplus Media LLC tarafından sunulan dijital hizmetler; iş gücü, zaman ve uzmanlık gerektirir. Bu nedenle, projenin başlamasından sonra yapılan iade talepleri, yapılan işin miktarına göre değerlendirilir.'
                },
                {
                    title: '2. İptal Koşulları',
                    content: 'Abonelik tabanlı hizmetler için iptal talepleri bir sonraki fatura döneminden önce yapılmalıdır. Halihazırda faturalandırılmış dönemler için iade yapılmaz.'
                },
                {
                    title: '3. İade Süreci',
                    content: 'Hizmet sunulmaması veya ciddi teknik kusurlar gibi durumlarda, 7 iş günü içerisinde iade talebinde bulunabilirsiniz. Talepleriniz 30 gün içerisinde incelenir ve sonuçlandırılır.'
                },
                {
                    title: '4. İletişim',
                    content: 'Tüm iade ve iptal taleplerinizi info@cplus.com.tr adresine iletmeyi unutmayın.'
                }
            ]
        },
        en: {
            title: 'Refund & Cancellation Policy',
            lastUpdated: 'Last Updated: March 25, 2026',
            sections: [
                {
                    title: '1. Digital Services',
                    content: 'Digital services provided by Cplus Media LLC require labor, time, and expertise. Therefore, refund requests after the project has started are evaluated based on the amount of work completed.'
                },
                {
                    title: '2. Cancellation Terms',
                    content: 'Cancellation requests for subscription-based services must be made before the next billing cycle. No refunds are given for already-billed periods.'
                },
                {
                    title: '3. Refund Process',
                    content: 'In cases of non-delivery of service or severe technical defects, you may request a refund within 7 business days. Your requests will be reviewed and finalized within 30 days.'
                },
                {
                    title: '4. Contact',
                    content: 'Make sure to send all refund and cancellation requests to info@cplus.com.tr.'
                }
            ]
        }
    };

    const t = texts[language] || texts.tr;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-20 text-gray-800 dark:text-gray-100">
            <SEO 
                title={t.title}
                description="Cplus Media LLC Refund & Cancellation Policy"
            />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-gray-900 rounded-3xl p-8 md:p-12 shadow-xl border border-gray-200 dark:border-gray-800"
                >
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center text-green-600 dark:text-green-400">
                            <RefreshCcw className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t.title}</h1>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{t.lastUpdated}</p>
                        </div>
                    </div>

                    <div className="space-y-8">
                        {t.sections.map((section, idx) => (
                            <div key={idx} className="space-y-3">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                                    {section.title}
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg">
                                    {section.content}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 pt-8 border-t border-gray-100 dark:border-gray-800">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Contact Us: <a href="mailto:info@cplus.com.tr" className="text-blue-600 hover:underline">info@cplus.com.tr</a> | Phone: +1 (302) 417-2720
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Refund;
